/**
 * StatCard Component
 *
 * Composite stat card for dashboard header with:
 * - Badge backdrop overlay (decorative, low opacity)
 * - Primary count with semantic coloring
 * - Descriptive label
 * - TrendIndicator for period comparison
 * - Muted state when count is 0
 * - Entrance and attention animations
 *
 * Design Compliance:
 * - Polaris spacing scale (gap values)
 * - Semantic colors with purpose (critical=red, warning=amber, healthy=green)
 * - Equal card sizing with consistent layout
 * - Accessible button interactions
 * - Responsive design support
 */

import type { RefObject } from 'react';
import { TrendIndicator, type TrendData } from './TrendIndicator';
import type { StatCardType } from './StatCardActions';

export interface StatCardProps {
  /** Unique identifier matching semantic type */
  id: StatCardType;
  /** Display label (e.g., "Critical", "Warning") */
  label: string;
  /** Primary count value */
  value: number;
  /** Path to badge image */
  badgeImage: string;
  /** Optional trend data for period comparison */
  trend?: TrendData;
  /** Tab index for navigation target */
  tabIndex: number;
  /** Click handler for card selection */
  onClick?: (tabIndex: number, ref?: RefObject<HTMLDivElement>) => void;
  /** Optional ref for scroll targeting */
  scrollRef?: RefObject<HTMLDivElement>;
}

/**
 * StatCard - Enhanced stat card with backdrop and trends
 */
export function StatCard({
  id,
  label,
  value,
  badgeImage,
  trend,
  tabIndex,
  onClick,
  scrollRef,
}: StatCardProps) {
  const isEmpty = value === 0;
  
  // Determine if trend should invert tone (down is good for critical/warning alerts)
  const shouldInvertTone = id === 'critical' || id === 'warning';

  // Build dynamic class list for animations and states
  const cardClasses = [
    'dashboard-stat-card',
    `dashboard-stat-card--${id}`,
    'stat-badge-enter',
    // Muted state when count is 0
    isEmpty && 'dashboard-stat-card--empty',
    // Attention animation for critical/warning with active alerts
    id === 'critical' && value > 0 && 'stat-badge-attention--critical',
    id === 'warning' && value > 0 && 'stat-badge-attention--warning',
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    onClick?.(tabIndex, scrollRef);
  };

  return (
    <button
      type="button"
      className={cardClasses}
      onClick={handleClick}
      aria-label={`${label}: ${value}. Click to view details.`}
    >
      {/* Badge backdrop overlay - decorative, low opacity */}
      <div className="dashboard-stat-card__backdrop" aria-hidden="true">
        <img
          src={badgeImage}
          alt=""
          className="dashboard-stat-card__backdrop-img"
        />
      </div>

      {/* Content layer */}
      <div className="dashboard-stat-card__content">
        {/* Main info section */}
        <div className="dashboard-stat-card__info">
          <div className="dashboard-stat-card__primary">
            <span className="dashboard-stat-card__count">{value}</span>
            {/* Trend indicator next to count */}
            {trend && (
              <TrendIndicator
                value={trend.value}
                direction={trend.direction}
                period={trend.period}
                invertTone={shouldInvertTone}
                size="small"
              />
            )}
          </div>
          <span className="dashboard-stat-card__label">{label}</span>
        </div>
      </div>
    </button>
  );
}

export default StatCard;
